import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  DefaultCrypto,
  RedirectRequestHandler,
  LocalStorageBackend,
  BasicQueryStringUtils,
} from "@openid/appauth";

import {
  EUREKA_AUTHORIZATION_ENDPOINT,
  EUREKA_TOKEN_ENDPOINT,
  EUREKA_CLIENT_ID,
  EUREKA_SCOPES,
  EUREKA_REDIRECT_PATH,
  PKCE_VERIFIER_KEY_PREFIX,
} from "./constants";

let loginInFlight = false;
const crypto = new DefaultCrypto();
const queryStringUtils = new BasicQueryStringUtils();

const storageBackend = new LocalStorageBackend();

const handler = new RedirectRequestHandler(
  storageBackend,
  queryStringUtils,
  window.location,
  crypto,
);

const notifier = new AuthorizationNotifier();
handler.setAuthorizationNotifier(notifier);

const serviceConfig = new AuthorizationServiceConfiguration({
  authorization_endpoint: EUREKA_AUTHORIZATION_ENDPOINT,
  token_endpoint: EUREKA_TOKEN_ENDPOINT,
  revocation_endpoint: "", // not needed for our use case
});

export function getRedirectUri() {
  return `${window.location.origin}${EUREKA_REDIRECT_PATH}`;
}

export function startEurekaLogin() {
  if (loginInFlight) return;
  loginInFlight = true;

  const request = new AuthorizationRequest(
    {
      client_id: EUREKA_CLIENT_ID,
      redirect_uri: getRedirectUri(),
      scope: EUREKA_SCOPES.join(" "),
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
    },
    crypto,
    true, // usePKCE
  );

  // Persist verifier ourselves (keyed by state) so it’s easy to retrieve on callback
  const state = request.state;

  const codeVerifier: string | undefined = (request as any)?.internal
    ?.code_verifier;

  if (state && codeVerifier) {
    sessionStorage.setItem(PKCE_VERIFIER_KEY_PREFIX + state, codeVerifier);
  }

  handler.performAuthorizationRequest(serviceConfig, request);
}

let inFlight: Promise<{
  code: string;
  codeVerifier: string;
  redirectUri: string;
}> | null = null;

export function completeEurekaAuthIfPossible(): {
  code: string;
  codeVerifier: string;
  redirectUri: string;
} {
  const qs = new URLSearchParams(window.location.search);
  const code = qs.get("code");
  const state = qs.get("state");
  const err = qs.get("error");

  if (err) throw new Error(qs.get("error_description") || err);
  if (!code || !state) throw new Error("Missing code/state in callback URL");

  const handle = localStorage.getItem("appauth_current_authorization_request");
  if (!handle) throw new Error("Missing appauth_current_authorization_request");

  const raw = localStorage.getItem(`${handle}_appauth_authorization_request`);
  if (!raw) throw new Error("Missing stored authorization request");

  const stored = JSON.parse(raw);

  if (stored.state !== state) throw new Error("State mismatch");
  if (stored.redirect_uri !== getRedirectUri())
    throw new Error("Redirect URI mismatch");

  const codeVerifier = stored.internal?.code_verifier;
  if (!codeVerifier) throw new Error("Missing code_verifier");

  localStorage.removeItem(`${handle}_appauth_authorization_request`);
  localStorage.removeItem("appauth_current_authorization_request");

  return { code, codeVerifier, redirectUri: stored.redirect_uri };
}

export function completeEurekaAuthIfPossible2(): Promise<{
  code: string;
  codeVerifier: string;
  redirectUri: string;
}> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const qs = new URLSearchParams(window.location.search);
    const code = qs.get("code");
    const state = qs.get("state");

    if (!code || !state) {
      throw new Error("Not an OAuth callback URL (missing code/state)");
    }

    return await new Promise<{
      code: string;
      codeVerifier: string;
      redirectUri: string;
    }>((resolve, reject) => {
      notifier.setAuthorizationListener((request, response, error) => {
        if (error) {
          reject(
            new Error(
              error.errorDescription || error.error || "Authorization error",
            ),
          );
          return;
        }
        if (!response?.code) {
          reject(new Error("Missing authorization code"));
          return;
        }

        const verifier = (request as any)?.internal?.code_verifier as
          | string
          | undefined;
        if (!verifier) {
          reject(new Error("Missing PKCE code_verifier"));
          return;
        }

        resolve({
          code: response.code,
          codeVerifier: verifier,
          redirectUri: getRedirectUri(),
        });
      });

      handler.completeAuthorizationRequestIfPossible().catch(reject);
    });
  })();

  inFlight.finally(() => {
    inFlight = null;
  });

  return inFlight;
}
